import type { ExplorerItem } from "../hooks/useLawExplorer";

interface LawExplorerHeaderProps {
  item: ExplorerItem | null;
  isLoading: boolean;
  canGoBack: boolean;
  onNavigate: (type: "article" | "section", id: string) => void;
  onGoBack: () => void;
  onClose: () => void;
}

export function LawExplorerHeader({
  item,
  isLoading,
  canGoBack,
  onNavigate,
  onGoBack,
  onClose,
}: LawExplorerHeaderProps) {
  // Build breadcrumb segments
  const breadcrumbs = buildBreadcrumbs(item);

  return (
    <header className="law-explorer-header">
      <div className="law-explorer-header-top">
        {/* Back button (mobile) */}
        {canGoBack && (
          <button className="law-explorer-back" onClick={onGoBack} aria-label="Retour">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Breadcrumbs */}
        <nav className="law-explorer-breadcrumbs" aria-label="Fil d'Ariane">
          {isLoading ? (
            <span className="law-explorer-breadcrumb-loading">Chargement...</span>
          ) : breadcrumbs.length === 0 ? (
            <span className="law-explorer-breadcrumb-empty">-</span>
          ) : (
            breadcrumbs.map((crumb, index) => (
              <span key={crumb.id} className="law-explorer-breadcrumb-item">
                {index > 0 && <span className="law-explorer-breadcrumb-sep">/</span>}
                {crumb.isClickable ? (
                  <button
                    className="law-explorer-breadcrumb-link"
                    onClick={() => onNavigate(crumb.type, crumb.id)}
                    title={crumb.fullTitle}
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="law-explorer-breadcrumb-current" title={crumb.fullTitle}>
                    {crumb.label}
                  </span>
                )}
              </span>
            ))
          )}
        </nav>

        {/* Actions */}
        <div className="law-explorer-actions">
          {item && (
            <a
              href={item.legifranceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="law-explorer-external"
              title="Ouvrir dans Legifrance"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
          <button className="law-explorer-close" onClick={onClose} aria-label="Fermer">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Status bar (for articles) */}
      {item && item.type === "article" && (
        <div className="law-explorer-status-bar">
          {/* Status badge */}
          <span className={`law-explorer-status ${getStatusClass(item.etat)}`}>
            {formatStatus(item.etat)}
          </span>

          {/* IDCC badge (KALI only) */}
          {item.source === "kali" && item.idcc && (
            <span className="law-explorer-idcc">IDCC {item.idcc}</span>
          )}

          {/* Article number */}
          {item.num && <span className="law-explorer-article-num">{item.num}</span>}
        </div>
      )}
    </header>
  );
}

// ============================================================================
// Helpers
// ============================================================================

interface BreadcrumbItem {
  id: string;
  type: "article" | "section";
  label: string;
  fullTitle: string;
  isClickable: boolean;
}

function buildBreadcrumbs(item: ExplorerItem | null): BreadcrumbItem[] {
  if (!item) return [];

  const crumbs: BreadcrumbItem[] = [];

  if (item.type === "article") {
    if (item.source === "kali") {
      // KALI: Convention > Article
      if (item.conventionTitre) {
        crumbs.push({
          id: item.conteneurId || "",
          type: "section", // Convention treated as section for navigation
          label: truncate(item.conventionTitre, 30),
          fullTitle: item.conventionTitre,
          isClickable: false, // TODO: Support convention navigation
        });
      }

      // Current article
      crumbs.push({
        id: item.articleId,
        type: "article",
        label: item.titre || item.num || item.articleId,
        fullTitle: item.titre || item.num || item.articleId,
        isClickable: false,
      });
    } else {
      // LEGI: Code > Section(s) > Article
      crumbs.push({
        id: "code-travail",
        type: "section",
        label: "Code du travail",
        fullTitle: "Code du travail",
        isClickable: false,
      });

      // Ancestry sections
      for (const ancestor of item.ancestryPath) {
        crumbs.push({
          id: ancestor.id,
          type: "section",
          label: truncate(ancestor.titre, 25),
          fullTitle: ancestor.titre,
          isClickable: true,
        });
      }

      // Current article
      crumbs.push({
        id: item.articleId,
        type: "article",
        label: item.num || item.articleId,
        fullTitle: item.num || item.articleId,
        isClickable: false,
      });
    }
  } else {
    // Section view
    crumbs.push({
      id: "code-travail",
      type: "section",
      label: "Code du travail",
      fullTitle: "Code du travail",
      isClickable: false,
    });

    crumbs.push({
      id: item.sectionId,
      type: "section",
      label: item.titre || item.sectionId,
      fullTitle: item.titre || item.sectionId,
      isClickable: false,
    });
  }

  return crumbs;
}

function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 1) + "\u2026";
}

function getStatusClass(etat: string | null): string {
  if (!etat) return "unknown";
  const lower = etat.toLowerCase();
  if (lower.includes("vigueur")) return "active";
  if (lower.includes("abroge")) return "repealed";
  return "unknown";
}

function formatStatus(etat: string | null): string {
  if (!etat) return "Statut inconnu";
  const lower = etat.toLowerCase();
  if (lower.includes("vigueur")) return "En vigueur";
  if (lower.includes("abroge")) return "Abroge";
  return etat;
}
